import BigNumber from "bignumber.js";
import moment from "moment";
import Web3 from "web3";

export function isNumeric(num) {
  return !isNaN(num) && !isNaN(parseFloat(num));
}

export async function sleep(ms) {
  return new Promise((resolve) => setTimeout(() => resolve(), ms));
}

export function isInt(n) {
  return Number(n) === n && n % 1 === 0;
}

export function isFloat(n) {
  return Number(n) === n && n % 1 === 0;
}

export function shuffleArray(array) {
  const n = array.length;
  for (let i = n - 1; i > 0; --i) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.toLowerCase().slice(1);
}

export function isNullAddress(address) {
  return "0x0000000000000000000000000000000000000000" === String(address);
}

export function isValidAddress(address) {
  return Web3.utils.isAddress(address);
}

export function isValidPassword(password) {
  const regex = /\w{1,}/;
  return regex.test(password);
}

export function formatAddress(address, prefix = 4) {
  return address.slice(0, prefix + 2) + "..." + address.slice(-prefix);
}

export function formatTokenId(id, length = 6) {
  const preLength = length - String(id).length < 0 ? 0 : length - String(id).length;
  return "0".repeat(preLength) + String(id);
}

export function formatCollectionId(id, length = 6) {
  return "C" + formatTokenId(id, length);
}

export function formatPrice(price) {
  if (price < 1e6) return numberWithCommas(Number(price.toFixed(4)));
  if (price < 1e15) {
    const suffixes = ["", "K", "M", "B", "T"];
    const suffixNum = Math.floor((("" + parseInt(price)).length - 1) / 3);
    let shortValue = parseFloat((price / Math.pow(1000, suffixNum)).toFixed(1));
    return shortValue + suffixes[suffixNum];
  }
  return BigNumber(price).toExponential(1);
}

export function compactNumber(n, fractionDigits = 1) {
  const suffixes = ["", "K", "M", "B", "T"];
  const suffixNum = Math.floor((("" + parseInt(n)).length - 1) / 3);
  let shortValue = parseFloat((n / Math.pow(1000, suffixNum)).toPrecision(fractionDigits + 1));
  if (shortValue % 1 !== 0) shortValue = shortValue.toFixed(fractionDigits);
  return shortValue + suffixes[suffixNum];
}

export function numberWithCommas(x, fractionDigits) {
  const [naturalPart, decimalPart] = x.toString().split(".");
  let out = naturalPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (decimalPart) {
    if (!isNumeric(fractionDigits)) out += "." + decimalPart;
    else if (decimalPart.length >= fractionDigits) out += "." + decimalPart.substr(0, fractionDigits);
    else out += "." + decimalPart + "0".repeat(fractionDigits - decimalPart.length);
  }
  return out;
}

export function getSelectedMarket() {
  let market = window.localStorage.getItem("market");
  if (!market) {
    setSelectedMarket("trava");
    market = "trava";
  }
  return market;
}

export function setSelectedMarket(market) {
  window.localStorage.setItem("market", market);
}

export function timeHasPassed(timestamp) {
  const delta = moment().diff(moment(timestamp));
  if (delta < 60000) {
    return `${Math.floor(delta / 60000)}s ago`;
  } else if (delta < 3600000) {
    const duration = moment.duration(delta);
    const s = duration.seconds();
    const m = duration.minutes();
    return `${m}m ${s}s ago`;
  } else if (delta < 86400000) {
    const duration = moment.duration(delta);
    const h = duration.hours();
    return `${h} hours ago`;
  } else {
    const duration = moment.duration(delta);
    const day = Math.floor(duration.asDays());
    return `${day} days ago`;
  }
}

export function timeHasPassedReduce(timestamp) {
  const diff = moment().diff(moment(new Date(timestamp)));
  const duration = moment.duration(diff);
  if (diff > 86400000) {
    const d = Math.floor(duration.asDays());
    return `${d} days ago`;
  } else if (diff > 3600000) {
    const h = duration.hours();
    return `${h} hours ago`;
  } else if (diff > 60000) {
    const m = duration.minutes();
    return `${m} mins ago`;
  } else if (diff > 0) {
    const s = duration.seconds();
    return `${s} seconds ago`;
  }
  return "a few seconds ago";
}

export function formatDate(toDate, targetFormatMask, sourceFormatMask) {
  const countdownMillisecond = Math.max(0, getDelta(toDate, sourceFormatMask));
  const duration = moment.duration(countdownMillisecond);
  const countdownString = duration.format(targetFormatMask);
  return [countdownMillisecond, countdownString];
}

export function getDelta(toDate, sourceFormatMask) {
  if (!moment.isMoment(toDate)) {
    const convert = moment.isDate(toDate) ? moment(toDate) : moment(toDate, sourceFormatMask);
    return convert.diff(moment());
  }
  return toDate.diff(moment());
}

export function getVeTravaRank(votingPower) {
  if (!votingPower || !isNumeric(votingPower)) return "---";
  else if (votingPower <= 1000) return 1;
  else if (votingPower <= 10000) return 2;
  else if (votingPower <= 30000) return 3;
  else if (votingPower <= 100000) return 4;
  else return 5;
}

export function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item) && item != null;
}

export function mergeDeep(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key], source[key]);
      } else Object.assign(target, { [key]: source[key] });
    }
  }
  return mergeDeep(target, ...sources);
}

export function toFixed(num, fractionDigits = 0) {
  num = Number(num);
  if (num == 0) return Number(num.toFixed(fractionDigits));
  const fixedNum = Number(num.toFixed(fractionDigits));
  if (fixedNum == 0) return toFixed(num, fractionDigits + 1);
  return fixedNum;
}

export function getDifferentBetweenDate(time1, time2) {
  const n = time2 - time1;
  const days = Math.floor(n / 86400);
  let remain = n - days * 86400;
  const hour = Math.floor(remain / 3600);
  remain = remain - hour * 3600;
  const minute = Math.floor(remain / 60);
  remain = toFixed(remain - minute * 60);
  return { days, hour, minute, second: remain < 1 ? 0 : remain };
}

export function collectionSort(item1, item2) {
  if (item1.rarity < item2.rarity) return 1;
  else return -1;
}

function getPosition(string, subString, index) {
  return string.split(subString, index).join(subString).length;
}

export function detectError(error, defaultError = "Transaction went wrong") {
  try {
    const sError = error?.message;
    const firstPosition = getPosition(sError, "{", 2);
    const latestPosition = getPosition(sError, "}", 2) + 1;
    let perfectError = sError.slice(firstPosition, latestPosition) + "}";
    perfectError = JSON.parse(perfectError);
    return JSON.stringify(perfectError?.data?.message ?? defaultError);
  } catch (_) {
    return defaultError;
  }
}
